/**
 * App Context Component
 *
 * Displays the current app name, page title, and optional breadcrumb navigation.
 * Can be updated via props or event bus for dynamic navigation.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { BreadcrumbItem, NavigationContextUpdate } from '../types';
import { eventBus, Events } from '../services/events';

@customElement('moya-app-context')
export class MoyaAppContext extends LitElement {
  static styles = css`
    :host {
      display: flex;
      align-items: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      min-width: 0;
    }

    .app-context {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      overflow: hidden;
    }

    .app-name {
      font-size: 14px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      white-space: nowrap;
    }

    .separator {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.4);
      flex-shrink: 0;
    }

    .page-title {
      font-size: 14px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.7);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .breadcrumbs {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      overflow: hidden;
    }

    .breadcrumb-item {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    .breadcrumb-link {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
      text-decoration: none;
      white-space: nowrap;
      transition: color 0.15s ease;
      cursor: pointer;
    }

    .breadcrumb-link:hover {
      color: rgba(255, 255, 255, 0.9);
    }

    .breadcrumb-current {
      font-size: 14px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .breadcrumb-icon {
      width: 14px;
      height: 14px;
      opacity: 0.7;
      flex-shrink: 0;
    }

    .breadcrumb-separator {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.3);
      flex-shrink: 0;
    }

    /* Responsive: hide breadcrumbs on small screens */
    @media (max-width: 640px) {
      .breadcrumbs {
        display: none;
      }

      .app-name + .separator {
        display: none;
      }
    }

    /* CSS Variables for theming */
    :host {
      --app-context-text-primary: rgba(255, 255, 255, 0.9);
      --app-context-text-secondary: rgba(255, 255, 255, 0.7);
      --app-context-text-muted: rgba(255, 255, 255, 0.4);
      --app-context-link-color: rgba(255, 255, 255, 0.6);
      --app-context-link-hover: rgba(255, 255, 255, 0.9);
    }

    .app-name {
      color: var(--app-context-text-primary);
    }

    .page-title {
      color: var(--app-context-text-secondary);
    }

    .separator {
      color: var(--app-context-text-muted);
    }

    .breadcrumb-link {
      color: var(--app-context-link-color);
    }

    .breadcrumb-link:hover {
      color: var(--app-context-link-hover);
    }
  `;

  @property({ type: String }) appName = '';
  @property({ type: String }) pageTitle = '';
  @property({ type: Array }) breadcrumbs: BreadcrumbItem[] = [];
  @property({ type: Boolean }) showBreadcrumbs = false;
  @property({ type: Boolean }) showAppName = true;

  // Internal event handler cleanup — every subscription must be tracked here
  // so disconnectedCallback can remove it (otherwise handlers capturing the
  // detached element leak on every disconnect/reconnect).
  private unsubscribers: Array<() => void> = [];

  connectedCallback() {
    super.connectedCallback();

    // Listen for navigation context updates from child apps
    this.unsubscribers.push(eventBus.on(
      Events.NAVIGATION_CONTEXT_UPDATE,
      (data: unknown) => {
        const update = data as NavigationContextUpdate;
        if (update.appName !== undefined) {
          this.appName = update.appName;
        }
        if (update.pageTitle !== undefined) {
          this.pageTitle = update.pageTitle;
        }
        if (update.breadcrumbs !== undefined) {
          this.breadcrumbs = update.breadcrumbs;
        }
      }
    ));

    // Also listen for individual page title changes
    this.unsubscribers.push(eventBus.on(Events.PAGE_TITLE_CHANGED, (data: unknown) => {
      const { title } = data as { title: string };
      this.pageTitle = title;
    }));

    // And breadcrumb changes
    this.unsubscribers.push(eventBus.on(Events.BREADCRUMBS_CHANGED, (data: unknown) => {
      const { breadcrumbs } = data as { breadcrumbs: BreadcrumbItem[] };
      this.breadcrumbs = breadcrumbs;
    }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
  }

  private handleBreadcrumbClick(item: BreadcrumbItem, index: number) {
    this.dispatchEvent(
      new CustomEvent('moya-breadcrumb-click', {
        bubbles: true,
        composed: true,
        detail: { item, index },
      })
    );

    // If the breadcrumb has an href and no external handler, navigate
    if (item.href) {
      // Emit event for parent to handle navigation
      eventBus.emit(Events.NAVIGATION_CONTEXT_UPDATE, {
        pageTitle: item.label,
      });
    }
  }

  private renderBreadcrumbs() {
    if (!this.showBreadcrumbs || this.breadcrumbs.length === 0) {
      return '';
    }

    return html`
      <div class="breadcrumbs">
        ${this.breadcrumbs.map((item, index) => {
          const isLast = index === this.breadcrumbs.length - 1;

          return html`
            <div class="breadcrumb-item">
              ${item.icon
                ? html`<span class="breadcrumb-icon">${item.icon}</span>`
                : ''}
              ${isLast
                ? html`<span class="breadcrumb-current">${item.label}</span>`
                : item.href
                  ? html`<a
                      class="breadcrumb-link"
                      href="${item.href}"
                      @click="${(e: Event) => {
                        e.preventDefault();
                        this.handleBreadcrumbClick(item, index);
                      }}"
                      >${item.label}</a
                    >`
                  : html`<span
                      class="breadcrumb-link"
                      @click="${() => this.handleBreadcrumbClick(item, index)}"
                      >${item.label}</span
                    >`}
              ${!isLast
                ? html`<span class="breadcrumb-separator">/</span>`
                : ''}
            </div>
          `;
        })}
      </div>
    `;
  }

  render() {
    const hasContent =
      this.pageTitle ||
      (this.showBreadcrumbs && this.breadcrumbs.length > 0);

    return html`
      <div class="app-context">
        ${this.showAppName && this.appName
          ? html`<span class="app-name">${this.appName}</span>`
          : ''}
        ${this.showAppName && this.appName && hasContent
          ? html`<span class="separator">›</span>`
          : ''}
        ${this.showBreadcrumbs && this.breadcrumbs.length > 0
          ? this.renderBreadcrumbs()
          : this.pageTitle
            ? html`<span class="page-title">${this.pageTitle}</span>`
            : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-app-context': MoyaAppContext;
  }
}
